import * as React from 'react';
import { useSelector } from 'react-redux';

import { navigateToPage } from '@cian/valuation-utils-component';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';

import { MyHomeListing, MyHomeWithSliderListing } from '../../components/MyHome/MyHomeListing';
import { ListCard } from '../../components/MyHome/ListCard';
import { SpecialListCard } from '../../components/MyHome/SpecialListCard';
import { VIEWED_TIMEOUT } from '../../constants/viewedBlock';
import { selectIsDesktop, selectMyHomeId } from '../../selectors/page';
import { getArrayChunks } from '../../utils/array';
import { useTracking } from './hooks/useTracking';
import { useGetMyHomeListing } from './hooks/useGetMyHomeListing';

export const MyHomeListingContainer: React.FC = () => {
  const myHomeItems = useGetMyHomeListing();
  const myHomeId = useSelector(selectMyHomeId);
  const isDesktop = useSelector(selectIsDesktop);

  const { trackGoToOtherObjectClick, trackMyHomeListringVisible } = useTracking();

  const onGoToOtherMyHomeCard = React.useCallback(
    (id: number) => {
      trackGoToOtherObjectClick();
      navigateToPage(`/my-home/?myHomeId=${id}`);
    },
    [myHomeId, trackGoToOtherObjectClick],
  );

  const onGoToBackMyHomeCard = React.useCallback(() => {
    navigateToPage(`/my-home/?myHomeId=${myHomeId}`);
  }, [myHomeId]);

  if (!myHomeItems) {
    return null;
  }

  /**
   * Если карточек меньше чем 3 или мобильный веб - слайдер не требуется
   */
  if (myHomeItems.length < 3 || !isDesktop) {
    return (
      <ActionAfterViewObserver callback={trackMyHomeListringVisible} callbackDelay={VIEWED_TIMEOUT}>
        <MyHomeListing>
          {myHomeItems.map(item => {
            return (
              <ListCard
                key={item.id}
                onClick={() => onGoToOtherMyHomeCard(item.id)}
                isActive={item.id === myHomeId}
                redPoint={item.redPoint}
                address={item.address}
                title={item.title}
              />
            );
          })}
          <SpecialListCard onClick={onGoToBackMyHomeCard} />
        </MyHomeListing>
      </ActionAfterViewObserver>
    );
  }

  const chipsChunks = getArrayChunks(myHomeItems, 3);

  /**
   * Добавляем служебную "чипсу" - "Добавить объект" после каждой 3й карточки,
   * если карточек меньше 3х, не добавляем, чтобы не показывать 2 раза "Добавить объект"
   * в одном слайде из 4х карточек
   */
  const chipsRender = chipsChunks
    .map((chipsChunk, i) => {
      return [
        ...chipsChunk.map(item => {
          return (
            <ListCard
              key={item.id}
              onClick={() => onGoToOtherMyHomeCard(item.id)}
              isActive={item.id === myHomeId}
              redPoint={item.redPoint}
              address={item.address}
              title={item.title}
            />
          );
        }),
        chipsChunk.length === 3 ? <SpecialListCard key={i} onClick={onGoToBackMyHomeCard} /> : null,
      ];
    })
    .flat();

  return (
    <ActionAfterViewObserver callback={trackMyHomeListringVisible} callbackDelay={VIEWED_TIMEOUT}>
      <MyHomeWithSliderListing>{chipsRender}</MyHomeWithSliderListing>
    </ActionAfterViewObserver>
  );
};

MyHomeListingContainer.displayName = 'MyHomeListingContainer';
